import { Box, xcss } from '@atlaskit/primitives';
import { getConfluencePageAriFromContentRef } from '@databases/connections/utils/ariHelper';
import { messages } from '@databases/intl/messages';
import { useCallbackEffect, useLoggingCallback } from '@databases/react';
import { defaultStoreUtils } from '@databases/redux';
import { selectDatabaseDraftByContentId } from '@databases/redux/changesets/selectors';
import connectionsSlice from '@databases/redux/connections/slice';
import { AsyncOperationStatus } from '@databases/redux-types';
import { type Optional } from '@databases/types';
import {
  type BlogpostReference,
  type ContentState,
  type PageReference,
} from '@databases/types/confluence';
import { type DatabaseFieldType } from '@databases/types/database';
import {
  getContentStateKey,
  uniqueContentStates,
} from '@databases/utils/confluence/contentState';
import { getContentKey } from '@databases/utils/confluence/utils';
import {
  applyPageStatus,
  getAllContentStates,
  getContentStatus,
  getReferencedStatusContentRefs,
} from '@databases/utils/database/field-types/pageStatus';
import { getDatabaseEntryRequired } from '@databases/utils/database/utils';
import {
  loadAvailableContentStates,
  loadContentRef,
} from '@databases/utils/redux/content/actions';
import {
  selectAvailableContentStates,
  selectAvailableContentStatesStatus,
  selectContentRefOptional,
} from '@databases/utils/redux/content/selectors';
import React, { type FunctionComponent, useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl-next';

import { PopoverEdit } from '../PopoverEdit';
import { usePopoverSoftSelection } from '../popover-edit-common/usePopoverSoftSelection';
import { type EditComponentProps } from '../types';

import PageStatusSelect from './PageStatusSelect';

const addDataSectionStyles = xcss({
  listStyleType: 'none',
  padding: '0',
});

const PageStatusPopoverEdit: FunctionComponent<
  EditComponentProps<DatabaseFieldType.PAGE_STATUS>
> = ({
  value,
  onFocus,
  onBlur,
  autoFocus,
  field,
  contentId,
  entryId,
  isRenderedInSidePanel,
  isBulkEdit,
}) => {
  const [referencedContentRefs] = defaultStoreUtils.useEqualSelector((state) => {
    const databaseDraft = selectDatabaseDraftByContentId(state, {
      contentId,
      includeDeleted: true,
    });
    const entry = entryId && getDatabaseEntryRequired(databaseDraft, entryId);
    const referencedContentRefs = entry
      ? getReferencedStatusContentRefs({ database: databaseDraft, field, entry }, state)
      : [];

    return [referencedContentRefs] as const;
  });
  usePopoverSoftSelection({
    entryId,
    fieldId: field.id,
    isRenderedInSidePanel,
  });

  const AddDataSection = useMemo(
    () => (
      <Box xcss={addDataSectionStyles} as={'ul'}>
        {referencedContentRefs.map((contentRef, i) => (
          <li key={`${getContentKey(contentRef)}${i}`}>
            <SinglePageStatusEdit
              {...{
                value,
                onFocus,
                onBlur,
                autoFocus,
                field,
                contentRef,
                contentId,
                entryId,
              }}
            />
          </li>
        ))}
      </Box>
    ),
    [referencedContentRefs, onFocus, onBlur, autoFocus, field, contentId, entryId, value]
  );

  return (
    <PopoverEdit
      isMultiSelect={false}
      hasCurrentData={false}
      addDataSection={AddDataSection}
      isRenderedInSidePanel={isRenderedInSidePanel}
      fieldWidth={field.width}
      isBulkEdit={isBulkEdit}
    />
  );
};

export default PageStatusPopoverEdit;

interface SinglePageStatusEditProps
  extends Pick<
    EditComponentProps<DatabaseFieldType.PAGE_STATUS>,
    'value' | 'onFocus' | 'onBlur' | 'autoFocus' | 'field' | 'contentId' | 'entryId'
  > {
  contentRef: PageReference | BlogpostReference;
}

const SinglePageStatusEdit: FunctionComponent<SinglePageStatusEditProps> = ({
  value,
  onFocus,
  onBlur,
  autoFocus,
  field,
  contentRef,
  contentId,
  entryId,
}) => {
  const { formatMessage } = useIntl();

  const [
    contentStatus,
    customOptions,
    spaceOptions,
    contentStatesStatus,
    isSpaceStatesEnabled,
    isCustomStatesEnabled,
    resolvedContentRef,
  ] = defaultStoreUtils.useEqualSelector((state) => {
    const databaseDraft = selectDatabaseDraftByContentId(state, {
      contentId,
      includeDeleted: true,
    });
    const resolvedContentRef = selectContentRefOptional(state, { contentRef });
    const { customContentStates, spaceContentStates } =
      (resolvedContentRef &&
        selectAvailableContentStates(state, {
          contentId: resolvedContentRef.contentId,
        })) ??
      {};
    const spaceContentStateKeys = (spaceContentStates ?? []).map((s) =>
      getContentStateKey(s)
    );
    const usedContentStates = getAllContentStates(
      databaseDraft,
      state,
      contentRef.spaceKey
    );
    const availableContentStatesStatus =
      resolvedContentRef &&
      selectAvailableContentStatesStatus(state, {
        contentId: resolvedContentRef.contentId,
      });
    const isCustomStatesEnabled = customContentStates != null;
    return [
      getContentStatus(contentRef, value, state),
      isCustomStatesEnabled
        ? uniqueContentStates([
            ...usedContentStates,
            ...(customContentStates ?? []),
          ]).filter((s) => !spaceContentStateKeys.includes(getContentStateKey(s)))
        : [],
      spaceContentStates ?? [],
      availableContentStatesStatus?.status,
      spaceContentStates != null,
      isCustomStatesEnabled,
      resolvedContentRef,
    ] as const;
  });

  const isLoading = [
    undefined,
    AsyncOperationStatus.INITIAL,
    AsyncOperationStatus.PENDING,
  ].includes(contentStatesStatus);

  const dispatch = defaultStoreUtils.useDispatch();
  useCallbackEffect(
    useLoggingCallback(async () => {
      if ([undefined, AsyncOperationStatus.INITIAL].includes(contentStatesStatus)) {
        const resolvedContentRef = await dispatch(loadContentRef(contentRef));
        if (resolvedContentRef) {
          await dispatch(loadAvailableContentStates(resolvedContentRef.contentId));
        }
      }
    }, [dispatch, contentRef, contentStatesStatus])
  );

  const [pendingContentStatus, setPendingContentStatus] = useState<Optional<
    ContentState,
    'id'
  > | null>();

  const writeOptimisticPageStatusUpdate = useCallback(
    (val: Optional<ContentState, 'id'> | undefined) => {
      if (!resolvedContentRef) {
        return;
      }
      const ari = getConfluencePageAriFromContentRef(resolvedContentRef);
      if (!ari) {
        return;
      }

      if (val) {
        dispatch(
          connectionsSlice.actions.addColumnValueToContent({
            ari,
            columnName: 'pageStatus',
            data: {
              type: 'pageStatus',
              text: val?.name ?? '',
              value: {
                text: val?.name ?? '',
                color: val?.color ?? '',
              },
            },
          })
        );
      } else {
        dispatch(
          connectionsSlice.actions.removeColumnValueFromContent({
            ari,
            columnName: 'pageStatus',
          })
        );
      }
    },
    [dispatch, resolvedContentRef]
  );

  const store = defaultStoreUtils.useStore();
  const handleChange = useCallback(
    async (val: Optional<ContentState, 'id'> | undefined) => {
      setPendingContentStatus(val ?? null);
      writeOptimisticPageStatusUpdate(val);

      try {
        await applyPageStatus(contentRef, val ?? null, store);
      } finally {
        setPendingContentStatus(undefined);
      }
    },
    [contentRef, store, writeOptimisticPageStatusUpdate]
  );

  return (
    <PageStatusSelect
      value={
        (pendingContentStatus !== undefined ? pendingContentStatus : contentStatus) ??
        undefined
      }
      customOptions={customOptions}
      spaceOptions={spaceOptions}
      onChange={handleChange}
      onFocus={onFocus}
      onBlur={onBlur}
      autoFocus={autoFocus}
      isCreatable={
        !!entryId && field.settings.creatable !== false && isCustomStatesEnabled
      }
      isLoading={isLoading || pendingContentStatus !== undefined}
      noOptionsMessage={
        isSpaceStatesEnabled || isCustomStatesEnabled
          ? undefined
          : formatMessage(messages['page-status.no-options'])
      }
    />
  );
};
